/* eslint-disable no-console */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Card, Table, Button, Modal, OverlayTrigger, Tooltip, Alert } from "react-bootstrap";
import { BsArrowDown, BsQuestionCircle } from "react-icons/bs";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import { useNavigate } from "react-router-dom";
import { Header, Loader } from "../../components";
import { DATOS_IPSOS, DATOS_POR_PAIS } from "../../constants/datosIpsos";
import { useAuthContext } from "../../context/auth";
import ipsosLogo from "../../img/ipsos-logo.png";
import { getEdificiosOperativosPublico, calculateImpactosEfectivos } from "../../utils";
import "./stylesSimuladorImpacto.scss";

const SimuladorImpacto = () => {
  const navigate = useNavigate();
  const { pais: paisContext } = useAuthContext();
  const pais = paisContext || "Chile";

  // Estados para edificios e inventario
  const [edificios, setEdificios] = useState([]);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [checkAll, setCheckAll] = useState(true);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [inventarioMode, setInventarioMode] = useState(null); // null | "toda" | "seleccionar"
  const [loading, setLoading] = useState(true);

  // Estados para configuración de campaña (null = no configurado)
  const [horasExhibicion, setHorasExhibicion] = useState(null);
  const [modoExhibicion, setModoExhibicion] = useState(null); // "fullday" | "am" | "pm" | "intervalos"
  const [selectedIntervalos, setSelectedIntervalos] = useState([]);
  const [showIntervalosModal, setShowIntervalosModal] = useState(false);
  const [duracionSpot, setDuracionSpot] = useState(null);
  const [frecuenciaPorHora, setFrecuenciaPorHora] = useState(null);
  const [diasCampana, setDiasCampana] = useState(null);
  const [duracionTrayecto, setDuracionTrayecto] = useState(null); // null = no configurado
  const [showExportModal, setShowExportModal] = useState(false);

  // Intervalos disponibles (30 min cada uno, de 7am a 9pm)
  const intervalosDisponibles = useMemo(() => {
    const intervals = [];
    for (let hour = 7; hour < 21; hour++) {
      intervals.push(
        {
          start: `${hour.toString().padStart(2, "0")}:00`,
          end: `${hour.toString().padStart(2, "0")}:30`,
        },
        {
          start: `${hour.toString().padStart(2, "0")}:30`,
          end: `${(hour + 1).toString().padStart(2, "0")}:00`,
        }
      );
    }
    return intervals;
  }, []);

  // Datos fijos de IPSOS
  const datosIpsos = DATOS_POR_PAIS[pais] || DATOS_IPSOS;

  // Cargar edificios
  useEffect(() => {
    const loadEdificios = async () => {
      try {
        setLoading(true);
        const data = await getEdificiosOperativosPublico({ pais });
        const sortedData = data.sort((a, b) => a.edificio.localeCompare(b.edificio));
        setEdificios(sortedData);

        const initialChecked = {};
        sortedData.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } catch (error) {
        console.error("Error al cargar edificios:", error);
      } finally {
        setLoading(false);
      }
    };

    loadEdificios();
  }, [pais]);

  // Calcular totales de edificios seleccionados
  const inventarioTotales = useMemo(() => {
    let ascensores = 0;
    let totems = 0;
    let espera = 0;
    let cowork = 0;
    let cantidad = 0;
    let audiencia = 0;
    // Audiencia por tipo de circuito (personas en edificios con ese tipo de pantalla)
    let audienciaAscensores = 0;
    let audienciaTotems = 0;
    let audienciaEspera = 0;
    let audienciaCowork = 0;

    edificios.forEach((ed) => {
      if (checkedBuildings[ed.id]) {
        cantidad++;
        const personas = ed.personas || 0;
        audiencia += personas;

        // Pantallas por tipo
        ascensores += ed.pantallas || 0;
        totems += ed.totems || 0;
        espera += ed.espera || 0;
        cowork += ed.cowork || 0;

        // Audiencia por tipo (personas del edificio si tiene ese tipo de pantalla)
        if (ed.pantallas > 0) audienciaAscensores += personas;
        if (ed.totems > 0) audienciaTotems += personas;
        if (ed.espera > 0) audienciaEspera += personas;
        if (ed.cowork > 0) audienciaCowork += personas;
      }
    });

    const totalPantallas = ascensores + totems + espera + cowork;

    return {
      ascensores,
      totems,
      espera,
      cowork,
      totalPantallas,
      cantidadEdificios: cantidad,
      totalAudiencia: audiencia,
      // Audiencia por circuito (para cálculo de impactos por tipo)
      audienciaAscensores,
      audienciaTotems,
      audienciaEspera,
      audienciaCowork,
    };
  }, [edificios, checkedBuildings]);

  // Toggle individual
  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Toggle todos
  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  // Ubicaciones únicas con cantidad de edificios
  const ubicacionesConCantidad = useMemo(() => {
    const ubicaciones = {};
    edificios.forEach((ed) => {
      if (!ubicaciones[ed.ubicacion]) {
        ubicaciones[ed.ubicacion] = { count: 0, ids: [] };
      }
      ubicaciones[ed.ubicacion].count++;
      ubicaciones[ed.ubicacion].ids.push(ed.id);
    });
    return Object.entries(ubicaciones)
      .map(([nombre, data]) => ({ nombre, ...data }))
      .sort((a, b) => b.count - a.count);
  }, [edificios]);

  // Toggle por ubicación
  const toggleUbicacion = useCallback(
    (ubicacion) => {
      const idsUbicacion = edificios.filter((ed) => ed.ubicacion === ubicacion).map((ed) => ed.id);
      const todosSeleccionados = idsUbicacion.every((id) => checkedBuildings[id]);

      setCheckedBuildings((prev) => {
        const newChecked = { ...prev };
        idsUbicacion.forEach((id) => {
          newChecked[id] = !todosSeleccionados;
        });
        return newChecked;
      });
    },
    [edificios, checkedBuildings]
  );

  // Cálculos (solo si está configurado)
  const calculos = useMemo(() => {
    if (!horasExhibicion || !duracionSpot || !frecuenciaPorHora || !diasCampana || !duracionTrayecto) {
      return null;
    }

    const programacionDiaria = horasExhibicion * 3600;
    const trayectosPorDia = datosIpsos.viajesPorDia * datosIpsos.trayectosPorViaje;
    const tiempoEnAscensor = trayectosPorDia * duracionTrayecto;

    // Factor de cobertura: Full Day (14h) cubre todos los viajes, AM/PM (7h) solo ~50%
    const factorCobertura = horasExhibicion >= 14 ? 1 : horasExhibicion / 14;
    const viajesEnVentana = datosIpsos.viajesPorDia * factorCobertura;
    const trayectosEnVentana = Math.round(viajesEnVentana * datosIpsos.trayectosPorViaje);

    const spotsPorDia = frecuenciaPorHora * horasExhibicion;
    const ocupacionSegundos = spotsPorDia * duracionSpot;
    const ocupacionPorcentaje = (ocupacionSegundos / programacionDiaria) * 100;

    // Probabilidad por trayecto usando distribución Poisson (usando trayecto seleccionado para display)
    // λ = spots esperados durante el trayecto = (frecuencia por segundo) × duración trayecto
    // P(ver al menos 1 spot) = 1 - e^(-λ)
    const spotsEsperados = (frecuenciaPorHora / 3600) * duracionTrayecto;
    const probAparicionTrayecto = 1 - Math.exp(-spotsEsperados);
    const probNoVerEnTrayecto = 1 - probAparicionTrayecto;
    const probNoVerEnDia = Math.pow(probNoVerEnTrayecto, trayectosEnVentana);
    const probImpactoDiario = 1 - probNoVerEnDia;

    // Probabilidad por viaje (2 trayectos: sube + baja)
    const probNoVerEnViaje = Math.pow(probNoVerEnTrayecto, 2);
    const probImpactoViaje = 1 - probNoVerEnViaje;

    const probImpactoConAtencion = probImpactoDiario * (datosIpsos.porcentajeAtencion / 100);

    // Frecuencia de asistencia promedio (IPSOS): 4.16 días/semana (no todos van 5 días)
    const diasPorSemana = datosIpsos.diasPorSemanaPromedio || 5;
    const factorAsistencia = diasPorSemana / 5; // ~0.83

    // =========================================================================
    // CÁLCULO DE IMPACTOS EFECTIVOS POR CIRCUITO
    // Usa la duración de trayecto seleccionada por el usuario para TODOS los circuitos
    // (el Cotizador standalone usa duraciones fijas; al exportar se pasa esta duración)
    // =========================================================================
    const paramsComunes = {
      horasExhibicion,
      frecuenciaPorHora,
      diasCampana,
      datosIpsos,
      duracionTrayecto, // Usa la duración seleccionada por el usuario
    };

    // Impactos por circuito usando la duración seleccionada
    const impactosAscensores = calculateImpactosEfectivos({
      ...paramsComunes,
      audienciaTotal: inventarioTotales.audienciaAscensores,
    });

    const impactosTotems = calculateImpactosEfectivos({
      ...paramsComunes,
      audienciaTotal: inventarioTotales.audienciaTotems,
    });

    const impactosEspera = calculateImpactosEfectivos({
      ...paramsComunes,
      audienciaTotal: inventarioTotales.audienciaEspera,
    });

    const impactosCowork = calculateImpactosEfectivos({
      ...paramsComunes,
      audienciaTotal: inventarioTotales.audienciaCowork,
    });

    // Sumar impactos de todos los circuitos (igual que el Cotizador)
    const impactosTotalesCampana =
      impactosAscensores.impactosTotalesCampana +
      impactosTotems.impactosTotalesCampana +
      impactosEspera.impactosTotalesCampana +
      impactosCowork.impactosTotalesCampana;

    // Impactos por persona promedio (basado en audiencia única total)
    const impactosTotalesPorPersona =
      inventarioTotales.totalAudiencia > 0 ? impactosTotalesCampana / inventarioTotales.totalAudiencia : 0;

    // Impactos diarios (dividir total por días de campaña)
    const impactosPorDia = impactosTotalesPorPersona / diasCampana / factorAsistencia;
    const impactosPorSemana = impactosPorDia * diasPorSemana;
    const impactosPorMes = impactosPorDia * 21 * factorAsistencia;

    return {
      programacionDiaria,
      horasProgramacion: horasExhibicion,
      trayectosPorDia,
      viajesEnVentana,
      trayectosEnVentana,
      factorCobertura,
      tiempoEnAscensor,
      spotsPorDia,
      ocupacionSegundos,
      ocupacionPorcentaje,
      spotsEsperados,
      probAparicionTrayecto: probAparicionTrayecto * 100,
      probImpactoViaje: probImpactoViaje * 100,
      probImpactoDiario: probImpactoDiario * 100,
      probImpactoConAtencion: probImpactoConAtencion * 100,
      impactosPorDia,
      impactosPorSemana,
      impactosPorMes,
      impactosTotalesPorPersona,
      impactosTotalesCampana, // Total de impactos de toda la campaña
      // Totales globales derivados (para sección "Impactos Efectivos Totales")
      impactosTotalesPorDia: impactosTotalesCampana / diasCampana,
      impactosTotalesPorSemana: (impactosTotalesCampana / diasCampana) * 5,
      impactosTotalesPorMes: (impactosTotalesCampana / diasCampana) * 21,
      diasPorSemana,
      factorAsistencia,
      // Desglose por circuito (para debug/display)
      impactosPorCircuito: {
        ascensores: Math.round(impactosAscensores.impactosTotalesCampana),
        totems: Math.round(impactosTotems.impactosTotalesCampana),
        espera: Math.round(impactosEspera.impactosTotalesCampana),
        cowork: Math.round(impactosCowork.impactosTotalesCampana),
      },
    };
  }, [duracionSpot, frecuenciaPorHora, diasCampana, duracionTrayecto, datosIpsos, horasExhibicion, inventarioTotales]);

  const formatNumber = (num, decimals = 2) =>
    num.toLocaleString("es-CL", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const formatPercent = (num, decimals = 2) => `${formatNumber(num, decimals)}%`;

  // Períodos predefinidos para Plazo (días hábiles)
  const periodosPredefinidos = [
    { label: "1 Semana", dias: 5 },
    { label: "2 Semanas", dias: 10 },
    { label: "1 Mes", dias: 21 },
    { label: "3 Meses", dias: 63 },
  ];

  // Opciones de duración del spot
  const duracionesRow1 = [6, 7, 8, 9, 10];
  const duracionesRow2 = [11, 12, 13, 14, 15];

  // Frecuencias por hora
  const frecuencias = [5, 10, 15, 20, 25, 30];

  // Duraciones de trayecto (segundos) - para diferentes tipos de edificios
  const duracionesTrayecto = [
    { valor: 30, label: "30s" },
    { valor: 45, label: "45s" },
    { valor: 60, label: "60s" },
    { valor: 75, label: "75s" },
    { valor: 90, label: "90s" },
    { valor: 120, label: "120s" },
  ];

  // Obtener label del horario para mostrar en modal y exportar
  const getHorarioLabel = useCallback(() => {
    if (modoExhibicion === "fullday") return "Full Day";
    if (modoExhibicion === "am") return "AM";
    if (modoExhibicion === "pm") return "PM";
    if (modoExhibicion === "intervalos") return "Intervalos";
    return "-";
  }, [modoExhibicion]);

  // Confirmar exportación al cotizador
  const handleConfirmarExport = useCallback(() => {
    if (!calculos) return;

    // Construir edificios seleccionados por tipo de pantalla
    const edificiosSeleccionados = edificios
      .filter((ed) => checkedBuildings[ed.id])
      .reduce(
        (acc, ed) => {
          if (ed.pantallas > 0) acc.ascensores.push(ed.id);
          if (ed.totems > 0) acc.totems.push(ed.id);
          if (ed.espera > 0) acc.espera.push(ed.id);
          if (ed.cowork > 0) acc.cowork.push(ed.id);
          return acc;
        },
        { ascensores: [], totems: [], espera: [], cowork: [] }
      );

    // Determinar label y opciones según modo de exhibición
    let horarioLabel;
    let intervalosExport = [];
    if (modoExhibicion === "fullday") {
      horarioLabel = "FULL DAY | 07:00 a 21:00";
    } else if (modoExhibicion === "am") {
      horarioLabel = "AM | 07:00 a 14:00";
    } else if (modoExhibicion === "pm") {
      horarioLabel = "PM | 14:00 a 21:00";
    } else if (modoExhibicion === "intervalos") {
      horarioLabel = "INTERVALOS";
      intervalosExport = selectedIntervalos.map((i) => `${i.start} - ${i.end}`);
    }

    const simuladorConfig = {
      plazo: diasCampana,
      frecuencia: String(frecuenciaPorHora),
      duracion: duracionSpot,
      horasSeleccionadas: {
        horas: horasExhibicion,
        intervalos: intervalosExport,
        options: [{ label: horarioLabel, hours: horasExhibicion }],
      },
      isDigitalSelected: true,
      isProgrammaticSelected: false,
      edificiosSeleccionados,
    };

    setShowExportModal(false);
    navigate("/admin/online-cotizador", { state: { simuladorConfig } });
  }, [
    calculos,
    edificios,
    checkedBuildings,
    diasCampana,
    frecuenciaPorHora,
    duracionSpot,
    horasExhibicion,
    modoExhibicion,
    selectedIntervalos,
    navigate,
  ]);

  if (loading) return <Loader />;

  return (
    <div className="simulador-impacto-page">
      {/* Header */}
      <Header
        leftContent={
          <div className="d-flex align-items-center gap-2">
            <h1 className="header-title mb-0">Simulador de Probabilidad de Impacto</h1>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="tooltip-help" className="tooltip-help-simulador">
                  Calcula la probabilidad de impacto publicitario en ascensores usando datos de comportamiento IPSOS.
                </Tooltip>
              }
            >
              <span className="help-icon">
                <BsQuestionCircle size={18} />
              </span>
            </OverlayTrigger>
          </div>
        }
        centerContent={
          calculos && (
            <Button variant="primary" onClick={() => setShowExportModal(true)} className="btn-export-cotizador">
              Exportar a Cotizador
            </Button>
          )
        }
      >
        <div className="d-flex align-items-center gap-4 ipsos-header-data">
          <div className="ipsos-stat">
            <span className="stat-value">{Math.round(datosIpsos.porcentajeAtencion)}%</span>
            <span className="stat-label">Atención</span>
          </div>
          <div className="ipsos-stat">
            <span className="stat-value">{Math.round(datosIpsos.satisfaccionMedio)}%</span>
            <span className="stat-label">Satisfacción</span>
          </div>
          <div className="ipsos-stat">
            <span className="stat-value">{Math.round(datosIpsos.recallPublicidad)}%</span>
            <span className="stat-label">Recordación</span>
          </div>
          <img src={ipsosLogo} alt="IPSOS" className="ipsos-logo-header" />
        </div>
      </Header>

      <Container fluid className="py-2">
        <div className="print-container">
          {/* Indicador de configuración */}
          {!calculos && (
            <Alert variant="light" className="config-hint mb-3 text-center">
              <BsArrowDown className="me-2" />
              <strong>Configure su campaña</strong> seleccionando las opciones en cada sección
              <BsArrowDown className="ms-2" />
            </Alert>
          )}

          {/* Configuración de Campaña */}
          <Card className="config-card mb-3">
            <Card.Body className="py-2">
              <Row className="g-0">
                {/* Exhibición (Horario) */}
                <Col className="config-section">
                  <div className="config-label">Exhibición</div>
                  <div className="config-content exhibicion-grid">
                    {/* Row 1: Full Day */}
                    <Button
                      variant={modoExhibicion === "fullday" ? "light" : "outline-light"}
                      size="sm"
                      onClick={() => {
                        setModoExhibicion("fullday");
                        setHorasExhibicion(14);
                        setSelectedIntervalos([]);
                      }}
                      className="btn-exhibicion btn-fullday"
                    >
                      Full Day
                    </Button>
                    {/* Row 2: AM | PM */}
                    <Button
                      variant={modoExhibicion === "am" ? "light" : "outline-light"}
                      size="sm"
                      onClick={() => {
                        setModoExhibicion("am");
                        setHorasExhibicion(7);
                        setSelectedIntervalos([]);
                      }}
                      className="btn-exhibicion btn-am"
                    >
                      AM
                    </Button>
                    <Button
                      variant={modoExhibicion === "pm" ? "light" : "outline-light"}
                      size="sm"
                      onClick={() => {
                        setModoExhibicion("pm");
                        setHorasExhibicion(7);
                        setSelectedIntervalos([]);
                      }}
                      className="btn-exhibicion btn-pm"
                    >
                      PM
                    </Button>
                    {/* Row 3: Intervalos */}
                    <Button
                      variant={modoExhibicion === "intervalos" ? "light" : "outline-light"}
                      size="sm"
                      onClick={() => {
                        setModoExhibicion("intervalos");
                        setShowIntervalosModal(true);
                      }}
                      className="btn-exhibicion btn-intervalos"
                    >
                      {modoExhibicion === "intervalos" && selectedIntervalos.length > 0
                        ? `Intervalos (${horasExhibicion}h)`
                        : "Intervalos"}
                    </Button>
                  </div>
                </Col>

                {/* Inventario */}
                <Col className="config-section">
                  <div className="config-label">Inventario</div>
                  <div className="config-content inventario-config">
                    <div className="inventario-buttons">
                      <Button
                        variant={inventarioMode === "toda" ? "light" : "outline-light"}
                        size="sm"
                        onClick={() => {
                          setInventarioMode("toda");
                          const allChecked = {};
                          edificios.forEach(({ id }) => {
                            allChecked[id] = true;
                          });
                          setCheckedBuildings(allChecked);
                          setCheckAll(true);
                        }}
                      >
                        Toda la Red
                      </Button>
                      <Button
                        variant={inventarioMode === "seleccionar" ? "light" : "outline-light"}
                        size="sm"
                        onClick={() => {
                          setInventarioMode("seleccionar");
                          setShowInventarioModal(true);
                        }}
                      >
                        Seleccionar
                      </Button>
                    </div>
                    {inventarioMode && (
                      <div className="inventario-summary">
                        <span>
                          <strong>{inventarioTotales.cantidadEdificios}</strong> edificios
                        </span>
                        <span>
                          <strong>{inventarioTotales.totalPantallas}</strong> pantallas
                        </span>
                        <span>
                          <strong>{formatNumber(inventarioTotales.totalAudiencia, 0)}</strong> personas
                        </span>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Plazo */}
                <Col className="config-section">
                  <div className="config-label">Plazo</div>
                  <div className="config-content">
                    <div className="plazo-grid">
                      {periodosPredefinidos.map((p) => (
                        <Button
                          key={p.dias}
                          variant={diasCampana === p.dias ? "light" : "outline-light"}
                          size="sm"
                          onClick={() => setDiasCampana(p.dias)}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </div>
                    <Form.Select
                      size="sm"
                      value={diasCampana || ""}
                      onChange={(e) => setDiasCampana(Number(e.target.value))}
                      className="dias-select mt-2"
                    >
                      <option value="" disabled>
                        Días...
                      </option>
                      {[...Array(260).keys()].map((i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1} días
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>

                {/* Frecuencia */}
                <Col className="config-section">
                  <div className="config-label">Frecuencia</div>
                  <div className="config-content">
                    <div className="frecuencia-grid">
                      {frecuencias.map((freq) => (
                        <Button
                          key={freq}
                          variant={frecuenciaPorHora === freq ? "light" : "outline-light"}
                          size="sm"
                          onClick={() => setFrecuenciaPorHora(freq)}
                        >
                          {freq}
                        </Button>
                      ))}
                    </div>
                    {calculos && <div className="freq-result">{calculos.spotsPorDia} spots/día/pantalla</div>}
                  </div>
                </Col>

                {/* Duración Spot */}
                <Col className="config-section">
                  <div className="config-label">Spot</div>
                  <div className="config-content">
                    <div className="duracion-grid">
                      {duracionesRow1.map((dur) => (
                        <Button
                          key={dur}
                          variant={duracionSpot === dur ? "light" : "outline-light"}
                          size="sm"
                          onClick={() => setDuracionSpot(dur)}
                        >
                          {dur}s
                        </Button>
                      ))}
                      {duracionesRow2.map((dur) => (
                        <Button
                          key={dur}
                          variant={duracionSpot === dur ? "light" : "outline-light"}
                          size="sm"
                          onClick={() => setDuracionSpot(dur)}
                        >
                          {dur}s
                        </Button>
                      ))}
                    </div>
                  </div>
                </Col>

                {/* Duración Trayecto */}
                <Col className="config-section">
                  <div className="config-label">Trayecto</div>
                  <div className="config-content">
                    <div className="trayecto-grid">
                      {duracionesTrayecto.map((t) => (
                        <Button
                          key={t.valor}
                          variant={duracionTrayecto === t.valor ? "light" : "outline-light"}
                          size="sm"
                          onClick={() => setDuracionTrayecto(t.valor)}
                          title={t.desc}
                        >
                          {t.label}
                        </Button>
                      ))}
                    </div>
                    {duracionTrayecto && <div className="trayecto-desc">{duracionTrayecto}s por viaje</div>}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Resultados - Solo si está configurado */}
          {calculos ? (
            <>
              <Row className="mb-2 gx-3">
                {/* Bloque 1: Probabilidad Diaria */}
                <Col lg={4}>
                  <Card className="h-100 results-highlight-card">
                    <Card.Header className="bg-dark text-white py-2">
                      <h6 className="mb-0 small">Probabilidad Diaria</h6>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column justify-content-center py-2 px-2">
                      {/* Nivel 1: Por trayecto */}
                      <div className="prob-row prob-level-1 text-center">
                        <div className="prob-text">
                          <div className="prob-label">Por trayecto</div>
                          <div className="prob-desc">Probabilidad en subida o bajada</div>
                        </div>
                        <div className="prob-number">{formatPercent(calculos.probAparicionTrayecto, 1)}</div>
                      </div>

                      {/* Nivel 2: Por viaje */}
                      <div className="prob-row prob-level-2 text-center">
                        <div className="prob-text">
                          <div className="prob-label">Por viaje</div>
                          <div className="prob-desc">2 trayectos: sube + baja</div>
                        </div>
                        <div className="prob-number">{formatPercent(calculos.probImpactoViaje, 1)}</div>
                      </div>

                      {/* Nivel 3: Por día */}
                      <div className="prob-row prob-level-3 text-center">
                        <div className="prob-text">
                          <div className="prob-label">Al menos 1 vez al día</div>
                          <div className="prob-desc">
                            {formatNumber(calculos.viajesEnVentana, 1)} viajes/día
                            {calculos.factorCobertura < 1 &&
                              ` (${Math.round(calculos.factorCobertura * 100)}% cobertura)`}
                          </div>
                        </div>
                        <div className="prob-number">{formatPercent(calculos.probImpactoDiario, 1)}</div>
                      </div>

                      {/* Nivel 4: Impacto efectivo diario */}
                      <div className="prob-row prob-level-4 text-center">
                        <div className="prob-text">
                          <div className="prob-label">Impacto Efectivo Diario</div>
                          <div className="prob-desc">
                            Ajustado por {Math.round(datosIpsos.porcentajeAtencion)}% de atención (IPSOS)
                          </div>
                        </div>
                        <div className="prob-number text-success">
                          {formatPercent(calculos.probImpactoConAtencion, 1)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Bloque 2: Impactos Efectivos por Persona */}
                <Col lg={4}>
                  <Card className="h-100 results-impact-card">
                    <Card.Header className="bg-dark text-white py-2">
                      <h6 className="mb-0 small">Impactos Efectivos por Persona</h6>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column justify-content-center py-2 px-2">
                      {/* Nivel 1: Por día */}
                      <div className="impact-row impact-level-1">
                        <div className="impact-text">
                          <div className="impact-label-row">Por día</div>
                        </div>
                        <div className="impact-number">{formatNumber(calculos.impactosPorDia, 2)}</div>
                      </div>

                      {/* Nivel 2: Por semana */}
                      <div className={`impact-row impact-level-2 ${diasCampana < 5 ? "impact-na" : ""}`}>
                        <div className="impact-text">
                          <div className="impact-label-row">Por semana</div>
                          <div className="impact-desc">
                            {formatNumber(calculos.diasPorSemana, 1)} días prom. (IPSOS)
                          </div>
                        </div>
                        <div className="impact-number">{formatNumber(calculos.impactosPorSemana, 2)}</div>
                      </div>

                      {/* Nivel 3: Por mes */}
                      <div className={`impact-row impact-level-3 ${diasCampana < 21 ? "impact-na" : ""}`}>
                        <div className="impact-text">
                          <div className="impact-label-row">Por mes</div>
                          <div className="impact-desc">Según asistencia semanal (IPSOS)</div>
                        </div>
                        <div className="impact-number">{formatNumber(calculos.impactosPorMes, 2)}</div>
                      </div>

                      {/* Nivel 4: Por campaña */}
                      <div className="impact-row impact-level-4">
                        <div className="impact-text">
                          <div className="impact-label-row">Por campaña</div>
                          <div className="impact-desc">{diasCampana} días · Según asistencia semanal (IPSOS)</div>
                        </div>
                        <div className="impact-number">{formatNumber(calculos.impactosTotalesPorPersona, 2)}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Bloque 3: Impactos Efectivos Totales */}
                <Col lg={4}>
                  <Card className="h-100 results-total-card">
                    <Card.Header className="bg-dark text-white py-2">
                      <h6 className="mb-0 small">Impactos Efectivos Totales</h6>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column justify-content-center py-2 px-2">
                      {/* Por día */}
                      <div className="total-row total-level-1">
                        <div className="total-text">
                          <div className="total-label-row">Por día</div>
                        </div>
                        <div className="total-number">{formatNumber(calculos.impactosTotalesPorDia, 0)}</div>
                      </div>

                      {/* Por semana */}
                      <div className={`total-row total-level-2 ${diasCampana < 5 ? "total-na" : ""}`}>
                        <div className="total-text">
                          <div className="total-label-row">Por semana</div>
                        </div>
                        <div className="total-number">{formatNumber(calculos.impactosTotalesPorSemana, 0)}</div>
                      </div>

                      {/* Por mes */}
                      <div className={`total-row total-level-3 ${diasCampana < 21 ? "total-na" : ""}`}>
                        <div className="total-text">
                          <div className="total-label-row">Por mes</div>
                        </div>
                        <div className="total-number">{formatNumber(calculos.impactosTotalesPorMes, 0)}</div>
                      </div>

                      {/* Por campaña */}
                      <div className="total-row total-level-4">
                        <div className="total-text">
                          <div className="total-label-row">Por campaña</div>
                          <div className="total-desc">{diasCampana} días</div>
                        </div>
                        <div className="total-number total-highlight">
                          {formatNumber(calculos.impactosTotalesCampana, 0)}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Sección Científica - Metodología */}
              <Card className="metodologia-card">
                <Card.Header className="bg-dark text-white">
                  <h6 className="mb-0">Metodología y Fundamento Científico</h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    {/* Bloque 1: Modelo Teórico */}
                    <Col md={4}>
                      <div className="metodo-block metodo-teoria">
                        <div className="metodo-block-header">Modelo de Cálculo</div>
                        <div className="metodo-block-body">
                          <div className="modelo-desc">
                            Probabilidad de ver al menos 1 spot durante cada trayecto en ascensor
                          </div>
                          <div className="formula-section">
                            <div className="formula">
                              P<sub>trayecto</sub> = 1 - e<sup>-λ</sup>
                            </div>
                            <div className="formula-vars">
                              <div>
                                <strong>λ</strong> = spots esperados = {formatNumber(calculos.spotsEsperados, 3)}
                              </div>
                              <div>
                                <strong>
                                  P<sub>día</sub>
                                </strong>{" "}
                                = 1 - (1 - P<sub>tray</sub>)<sup>n</sup>
                              </div>
                              <div>
                                <strong>n</strong> = {calculos.trayectosEnVentana} trayectos · <strong>A</strong> ={" "}
                                {Math.round(datosIpsos.porcentajeAtencion)}%
                              </div>
                            </div>
                          </div>
                          <div className="modelo-termino">Distribución de Poisson</div>
                        </div>
                      </div>
                    </Col>

                    {/* Bloque 2: Datos IPSOS */}
                    <Col md={4}>
                      <div className="metodo-block metodo-ipsos">
                        <div className="metodo-block-header">Datos IPSOS (Base: 400 casos)</div>
                        <div className="metodo-block-body">
                          <Table size="sm" className="params-detail-table mb-0">
                            <tbody>
                              <tr>
                                <td>Viajes promedio/día</td>
                                <td>{datosIpsos.viajesPorDia}</td>
                              </tr>
                              <tr>
                                <td>Trayectos por viaje</td>
                                <td>{datosIpsos.trayectosPorViaje}</td>
                              </tr>
                              <tr>
                                <td>Duración trayecto</td>
                                <td>{duracionTrayecto} segundos</td>
                              </tr>
                              <tr>
                                <td>Asistencia semanal</td>
                                <td>{formatNumber(calculos.diasPorSemana, 1)} días/sem</td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Col>

                    {/* Bloque 3: Parámetros de Campaña */}
                    <Col md={4}>
                      <div className="metodo-block metodo-campana">
                        <div className="metodo-block-header">Parámetros de Campaña</div>
                        <div className="metodo-block-body">
                          <Table size="sm" className="params-detail-table mb-0">
                            <tbody>
                              <tr>
                                <td>Programación diaria</td>
                                <td>{calculos.horasProgramacion} horas</td>
                              </tr>
                              <tr>
                                <td>Duración del spot</td>
                                <td>{duracionSpot} segundos</td>
                              </tr>
                              <tr>
                                <td>Frecuencia</td>
                                <td>{frecuenciaPorHora} veces/hora</td>
                              </tr>
                              <tr>
                                <td>Spots por día</td>
                                <td>{calculos.spotsPorDia}</td>
                              </tr>
                              <tr>
                                <td>Ocupación diaria</td>
                                <td>
                                  {formatNumber(calculos.ocupacionSegundos, 0)}s (
                                  {formatPercent(calculos.ocupacionPorcentaje, 2)})
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="metodologia-footer text-muted small mt-3">
                    Estudio IPSOS 2025 · Margen de error: &lt;5%
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <Card className="text-center py-5 bg-light">
              <Card.Body>
                <h5 className="text-muted mb-3">Configure los parámetros de su campaña</h5>
                <p className="text-muted mb-0">
                  Seleccione exhibición, plazo, frecuencia y duración para ver los resultados del simulador
                </p>
              </Card.Body>
            </Card>
          )}
        </div>
      </Container>

      {/* Modal de Intervalos */}
      <Modal show={showIntervalosModal} onHide={() => setShowIntervalosModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar Intervalos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Seleccione los intervalos de 30 minutos en los que desea simular la exhibición.
          </p>
          <div className="intervalos-grid">
            {intervalosDisponibles.map((intervalo, idx) => {
              const label = `${intervalo.start} - ${intervalo.end}`;
              const isSelected = selectedIntervalos.some((s) => s.start === intervalo.start && s.end === intervalo.end);
              return (
                <Button
                  key={idx}
                  variant={isSelected ? "primary" : "outline-secondary"}
                  size="sm"
                  className="intervalo-btn"
                  onClick={() => {
                    if (isSelected) {
                      setSelectedIntervalos((prev) =>
                        prev.filter((s) => s.start !== intervalo.start || s.end !== intervalo.end)
                      );
                    } else {
                      setSelectedIntervalos((prev) => [...prev, intervalo]);
                    }
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          <div className="mt-3 text-center">
            <strong>{selectedIntervalos.length}</strong> intervalos seleccionados ={" "}
            <strong>{selectedIntervalos.length * 0.5}</strong> horas
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              const horas = selectedIntervalos.length * 0.5;
              setHorasExhibicion(horas);
              setShowIntervalosModal(false);
            }}
            disabled={selectedIntervalos.length === 0}
          >
            Aplicar ({selectedIntervalos.length * 0.5}h)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Inventario */}
      <Modal show={showInventarioModal} onHide={() => setShowInventarioModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar Inventario</Modal.Title>
        </Modal.Header>
        <Modal.Body className="inventario-modal-body">
          <div className="d-flex justify-content-between align-items-center mb-3 inventario-summary">
            <div>
              <strong>{inventarioTotales.cantidadEdificios}</strong> edificios ·{" "}
              <strong>{inventarioTotales.totalPantallas}</strong> pantallas ·{" "}
              <strong>{formatNumber(inventarioTotales.totalAudiencia, 0)}</strong> ejecutivos
              <div className="small text-muted mt-1">
                Ascensores: {inventarioTotales.ascensores} · Tótems: {inventarioTotales.totems} · Espera:{" "}
                {inventarioTotales.espera} · Cowork: {inventarioTotales.cowork}
              </div>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={toggleCheckAll}>
              {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
            </Button>
          </div>
          <div className="ubicaciones-filter mb-3">
            <div className="ubicaciones-label small text-muted mb-2">Filtrar por ubicación:</div>
            <div className="ubicaciones-buttons d-flex flex-wrap gap-1">
              {ubicacionesConCantidad.map((ub) => {
                const todosSeleccionados = ub.ids.every((id) => checkedBuildings[id]);
                const algunoSeleccionado = ub.ids.some((id) => checkedBuildings[id]);
                return (
                  <Button
                    key={ub.nombre}
                    variant={
                      todosSeleccionados ? "primary" : algunoSeleccionado ? "outline-primary" : "outline-secondary"
                    }
                    size="sm"
                    className="ubicacion-btn"
                    onClick={() => toggleUbicacion(ub.nombre)}
                  >
                    {ub.nombre} ({ub.count})
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="table-responsive">
            <Table hover size="sm" className="inventario-table">
              <thead className="table-dark">
                <tr>
                  <th className="col-check">
                    <span onClick={toggleCheckAll} style={{ cursor: "pointer" }}>
                      {checkAll ? <ImCheckboxChecked size={14} /> : <ImCheckboxUnchecked size={14} />}
                    </span>
                  </th>
                  <th className="col-edificio">Edificio</th>
                  <th className="col-ubicacion">Ubicación</th>
                  <th className="col-pantallas text-center">Pantallas</th>
                  <th className="col-personas text-center">Ejecutivos</th>
                </tr>
              </thead>
              <tbody>
                {edificios.map((ed) => {
                  const total = (ed.pantallas || 0) + (ed.totems || 0) + (ed.espera || 0) + (ed.cowork || 0);
                  return (
                    <tr
                      key={ed.id}
                      onClick={() => toggleCheck(ed.id)}
                      className={checkedBuildings[ed.id] ? "table-success" : ""}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="col-check text-center">
                        {checkedBuildings[ed.id] ? <ImCheckboxChecked size={14} /> : <ImCheckboxUnchecked size={14} />}
                      </td>
                      <td className="col-edificio">{ed.edificio?.toUpperCase()}</td>
                      <td className="col-ubicacion">{ed.ubicacion}</td>
                      <td className="col-pantallas text-center fw-bold">{total || "-"}</td>
                      <td className="col-personas text-center">{ed.personas ? formatNumber(ed.personas, 0) : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInventarioModal(false)}>
            Aplicar selección
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación para exportar al cotizador */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Exportar simulación al Cotizador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table bordered size="sm">
            <tbody>
              <tr>
                <td className="fw-bold text-start">Edificios</td>
                <td className="text-end">{inventarioTotales.cantidadEdificios}</td>
              </tr>
              <tr>
                <td className="fw-bold text-start">Pantallas</td>
                <td className="text-end">{inventarioTotales.totalPantallas}</td>
              </tr>
              <tr>
                <td className="fw-bold text-start">Plazo</td>
                <td className="text-end">{diasCampana} días</td>
              </tr>
              <tr>
                <td className="fw-bold text-start">Frecuencia</td>
                <td className="text-end">{frecuenciaPorHora} spots/hora</td>
              </tr>
              <tr>
                <td className="fw-bold text-start">Duración</td>
                <td className="text-end">{duracionSpot} segundos</td>
              </tr>
              <tr>
                <td className="fw-bold text-start">Horario</td>
                <td className="text-end">
                  {getHorarioLabel()} ({horasExhibicion}h)
                </td>
              </tr>
              <tr className="table-primary">
                <td className="fw-bold text-start">Impactos efectivos</td>
                <td className="text-end fw-bold">
                  {calculos ? Math.round(calculos.impactosTotalesCampana).toLocaleString("es-CL") : "-"}
                </td>
              </tr>
            </tbody>
          </Table>
          <Alert variant="info" className="mb-0 mt-3">
            <small>
              El cotizador utilizará duración de trayecto estándar de 45 segundos para el cálculo de impactos efectivos.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmarExport}>
            Confirmar y exportar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SimuladorImpacto;
